import React, { Component, memo } from "react";
import { connect } from "react-redux";
import moment from "moment";
import {
  Wrapper,
  KeyboardView,
  Copy,
  Bold,
  Row,
  ProgressBar,
  Container,
  Card
} from "app/NativeComponents/common";
import {
  NewHeader,
  InputBox,
  InlineButton,
  SelectItem,
  SmallLabel,
  SpinWrapper
} from "app/NativeComponents/snippets";

import {
  appRedirect,
  setExportModal,
  popSidePanel,
  popAllSidePanels,
  updateLead,
  dismissMobileKeyboard,
  numberWithCommas,
  getSourceOfTruth,
  pushSidePanel,
  renderDate,
  getExportActualCount,
  updateUserSettings,
  checkIfUserHasMetadata,
  submitMarketingForm,
  getExportSettingsForUser,
} from "app/NativeActions";

// Memoized sub-components for better performance
const ExportTypeSelector = memo(({
  exportType,
  onTypeChange,
  colors,
  hasAccessToPeopleData,
  onUpgradePrompt,
  onSelectColumns,
  device,
  export_settings_loading
}) => (
  <Wrapper>
    <SmallLabel>Select your export type:</SmallLabel>
    <Row style={{ paddingLeft: 20, paddingBottom: 20 }}>
      <InlineButton
        icon={
          !hasAccessToPeopleData
            ? "lock"
            : exportType === "contacts"
              ? "radio-button-checked"
              : "radio-button-unchecked"
        }
        onPress={() => {
          if (!hasAccessToPeopleData) {
            onUpgradePrompt();
          } else {
            onTypeChange("contacts");
          }
        }}
        selected={exportType === "contacts"}
        style={
          exportType === "contacts"
            ? {
              borderColor: colors.active_color,
              borderWidth: 1,
              borderStyle: "solid",
              marginLeft: 0
            }
            : { marginLeft: 0 }
        }
        tooltip="Your export will include each associated contact as one line. This will not include all the information for each property."
        tooltipPlacement="top"
      >
        Contacts
      </InlineButton>
      <InlineButton
        icon={
          exportType === "properties"
            ? "radio-button-checked"
            : "radio-button-unchecked"
        }
        onPress={() => onTypeChange("properties")}
        selected={exportType === "properties"}
        style={
          exportType === "properties"
            ? {
              borderColor: colors.active_color,
              borderWidth: 1,
              borderStyle: "solid",
              marginLeft: 0
            }
            : { marginLeft: 0 }
        }
        tooltip="Your export will include each property as one line. This will not include all the information for each contact associated with a property."
        tooltipPlacement="top"
      >
        Properties
      </InlineButton>
      <Wrapper style={{ marginLeft: device === "desktop" ? "auto" : 0, marginRight: 15 }}>
        <InlineButton
          disabled={export_settings_loading}
          onPress={onSelectColumns}>
          Select Columns
        </InlineButton>
      </Wrapper>
    </Row>
  </Wrapper>
));

const ExportLimitProgress = memo(({
  count,
  limit,
  resetDate,
  successColor
}) => {
  if (!limit || limit === "unlimited") return null;

  return (
    <Wrapper style={{ padding: 25 }}>
      <ProgressBar
        color={successColor}
        width={200}
        progress={count > 0 ? count / limit : 0}
      />
      <Copy style={{ fontSize: 10 }}>
        You've exported {numberWithCommas(count)} out of {numberWithCommas(limit)} leads this month.
        Duplicate exports of the same lead within the billing cycle won't count towards this total.
        You'll be automatically upgraded if you exceed your limit.
        Your account will reset on {renderDate(resetDate)}.
      </Copy>
    </Wrapper>
  );
});

class ExportLeads extends Component {
  constructor(props) {
    super(props);

    const exportType = this.getInitialExportType();

    this.state = {
      loading: false,
      loading_sales_form: false,
      submitted_sales_form: false,
      list_columns: this.getInitialColumns(exportType, {}),
      selected_columns: props.export_modal?.selected_columns || [],
      selected_leads: props.export_modal?.selected_leads || [],
      include_likely_owners: true,
      include_family: false,
      include_likely_renters: false,
      include_potential_property_owners: false,
      scrub_dnc: false,
      scrub_landline: false,
      scrub_wireless: false,
      deduplicate: true,
      remove_items_without_phone_numbers: false,
      page: "export_lists",
      column_search: "",
      emails: props.user.email,
      include_all_columns: exportType === "properties" ? true : false,
      export_type: exportType,
      export_actual_count: 0,
      export_actual_count_loading: true,
      export_settings_loading: false,
      export_settings: null,
      export_columns: []
    };

    this._emails = React.createRef();
    this.executeExport = this.executeExport.bind(this);
  }

  // Extract initial export type logic
  getInitialExportType() {
    return !checkIfUserHasMetadata("people_data") ? "properties" : "contacts";
  }


  // Extract initial columns logic
  getInitialColumns(exportType, exportColumns = {}) {

    if (!exportColumns) return [];
    if (exportType === "properties") {
      return exportColumns.property_export_columns || [];
    }
    return exportColumns.contact_export_columns || [];
  }

  componentDidMount() {
    this.initializeComponent();
  }

  async initializeComponent() {
    // Fetch source of truth
    this.props.getSourceOfTruth({
      token: this.props.token,
      no_loading: true
    });

    this.props.getExportSettingsForUser({
      token: this.props.token,
      onLoading: () => {
        this.setState({ export_settings_loading: true });
      },
      onError: () => {
        this.setState({ export_settings_loading: false });
      },
      onSuccess: results => {
        const s = results.export_settings || {};
        const preferredType = s.export_type || this.state.export_type;

        this.setState({
          export_columns: results.export_columns,
          export_settings: results.export_settings,
          export_settings_loading: false,
          export_type: preferredType,
          list_columns: this.getInitialColumns(preferredType, results.export_columns),
          include_likely_owners: s.include_likely_owners ?? true,
          include_family: s.include_family ?? false,
          include_likely_renters: s.include_likely_renters ?? false,
          include_potential_property_owners: s.include_potential_property_owners ?? false,
          scrub_dnc: s.scrub_dnc ?? false,
          scrub_landline: s.scrub_landline ?? false,
          scrub_wireless: s.scrub_wireless ?? false,
          deduplicate: s.deduplicate ?? true,
          remove_items_without_phone_numbers: s.remove_items_without_phone_numbers ?? false
        });
        this.fetchExportCount();

        // Initialize columns
        this.setState(prevState => ({
          selected_columns: prevState.list_columns.filter(column => column.export || column.default),
          export_type: prevState.export_type
        }));

        this.updateListColumns();
      }
    });
    // Fetch export count

  }

  fetchExportCount() {
    const { export_modal, token } = this.props;

    this.props.getExportActualCount({
      token,
      select_all: export_modal?.select_all,
      total_count: export_modal?.total_count,
      new_filters: export_modal?.new_filters,
      filters: export_modal?.filters,
      property_flags: export_modal?.property_flags,
      property_flags_and_or: export_modal?.property_flags_and_or,
      using_old_filters: export_modal?.using_old_filters,
      search: export_modal?.search,
      search_type: export_modal?.search_type,
      list_id: export_modal?.list_id,
      list_history_id: export_modal?.list_history_id,
      deal_ids: export_modal?.deal_ids,
      onError: () => {
        this.setState({ export_actual_count_loading: false });
      },
      onSuccess: results => {
        this.setState({
          export_actual_count: results.total_count_not_yet_exported,
          export_actual_count_loading: false
        });
      }
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const shouldUpdateColumns =
      prevState.export_type !== this.state.export_type ||
      (prevState.list_columns.length > 0 && this.state.list_columns.length === 0) ||
      prevState.export_columns !== this.state.export_columns;

    if (shouldUpdateColumns) {
      this.updateListColumns();
    }
  }

  updateListColumns = () => {
    const { export_type, export_columns } = this.state;
    let newListColumns = [];

    if (export_type === "properties") {
      newListColumns = export_columns?.property_export_columns || [];
    } else {
      newListColumns = export_columns?.contact_export_columns || [];
    }

    // If export is undefined (first load), seed from default flags
    newListColumns = newListColumns.map(col => ({
      ...col,
      export: (typeof col.export === "boolean")
        ? col.export
        : (export_type === "properties"
          ? !!col.default_checked_in_property_export
          : !!col.default_checked_in_contact_export)
    }));
    this.setState({ list_columns: newListColumns });
  };


  checkListItem = item => {
    const newListColumns = this.state.list_columns.map(column =>
      column.slug === item.slug ? { ...column, export: !column.export } : column
    );
    const newSelectedColumns = newListColumns.filter(col => col.export);
    this.setState({
      list_columns: newListColumns,
      selected_columns: newSelectedColumns
    });
  };


  updateListItemFormat(item, format) {
    if (item.main_column !== true) {
      this.setState({
        list_columns: this.state.list_columns.map(column => {
          if (column.slug === item.slug) {
            return { ...column, column_format: format };
          }
          return column;
        })
      });
    }
  }

  reorderItems({ previousIndex, nextIndex }) {
    const { list_columns } = this.state;
    const reordered_items = [...list_columns];
    const [removed] = reordered_items.splice(previousIndex, 1);
    reordered_items.splice(nextIndex, 0, removed);
    this.setState({ list_columns: reordered_items });
  }

  handleExportTypeChange = (type) => {
    const { export_columns } = this.state;
    const baseColumns = type === "properties"
      ? (export_columns?.property_export_columns || [])
      : (export_columns?.contact_export_columns || []);

    const seeded = baseColumns.map(col => ({
      ...col,
      export: (type === "contacts")
        // Contacts: trust backend flag; fallback to legacy
        ? (typeof col.export === "boolean"
          ? col.export
          : !!col.default_checked_in_contact_export)
        // Properties: trust backend (property_export_columns already prepped)
        : (typeof col.export === "boolean" ? col.export : true)
    }));


    this.setState({
      export_type: type,
      list_columns: seeded,
      selected_columns: seeded.filter(c => c.export),
      include_all_columns: type === "properties" ? true : false
    });
  };


  confirmUpdate() {
    dismissMobileKeyboard();
    const { source_of_truth, all_feature_metadata } = this.props;
    let currentLimit = source_of_truth?.subscription?.metadata?.export_limit;
    let previousExportCount = parseInt(
      source_of_truth?.current_limits?.number_of_leads_exported?.amount || 0
    );
    let totalToExport = parseInt(this.state.export_modal?.total_count);
    if (parseInt(this.state.export_actual_count) > 0) {
      totalToExport = parseInt(this.state.export_actual_count);
    }
    if (currentLimit !== "unlimited" && previousExportCount + totalToExport > currentLimit) {
      this.handleExportLimitExceeded(previousExportCount, totalToExport, currentLimit);
    } else {
      this.executeExport();
    }
  }

  handleExportLimitExceeded(currentCount, totalToExport, limit) {
    const { all_feature_metadata, source_of_truth } = this.props;

    // Find appropriate feature upsell
    let feature_upsell_info = null;

    if (
      source_of_truth?.subscription?.metadata?.export_limit !== "unlimited" &&
      currentCount + totalToExport >
      source_of_truth?.subscription?.metadata?.export_limit
    ) {

      for (let i = 0; i < all_feature_metadata?.length; i++) {
        if (
          all_feature_metadata[i].slug == "export_limit" &&
          all_feature_metadata[i].unlock_limit <= currentCount + totalToExport &&
          (all_feature_metadata[i].unlock_limit >
            feature_upsell_info?.unlock_limit ||
            feature_upsell_info == null)
        ) {
          feature_upsell_info = all_feature_metadata[i];
        }
      }
    }

    if (!checkIfUserHasMetadata("paid_plan")) {
      this.showUpgradePrompt();
    } else {
      this.showLimitExceededModal(currentCount, totalToExport, limit, feature_upsell_info);
    }
  }

  showUpgradePrompt = () => {
    this.props.pushSidePanel({
      slug: "purchase_plan_prompt",
      overlay_modal: true,
      locked: true,
      data: {
        title: "Export More Leads With DealMachine Pro.",
        dismiss_button: "No thanks. Let me continue without exporting these leads.",
        no_free: true,
        event_slug: "in_app_export_leads"
      }
    });
  };

  showLimitExceededModal(currentCount, totalToExport, limit, feature_upsell_info) {
    const { source_of_truth } = this.props;

    this.props.pushSidePanel({
      slug: "unlock_feature_modal",
      overlay: true,
      data: {
        feature_upsell_info,
        renderContent: () => (
          <Wrapper style={{ padding: 25 }}>
            <Copy>
              You have exported <Bold>{numberWithCommas(currentCount)} leads</Bold> this billing
              cycle. Exporting these <Bold>{numberWithCommas(totalToExport)}</Bold> leads will
              exceed your monthly limit of <Bold>{numberWithCommas(limit)}</Bold>.
              Upgrade to continue. Your account will reset on{" "}
              <Bold>{renderDate(source_of_truth?.subscription?.monthly_limit_end_date)}</Bold>.
            </Copy>
          </Wrapper>
        ),
        buttonText: "Upgrade And Export",
        confirmText: "Confirm Upgrade?",
        onSuccess: () => this.executeExport()
      }
    });
  }

  executeExport() {
    if (!this.props.export_modal?.modalAction) return;

    const exportData = this.buildExportData();

    this.props.export_modal.modalAction({
      ...exportData,
      onLoading: () => this.setState({ loading: true }),
      onError: () => this.setState({ loading: false }),
      onSuccess: () => {
        this.setState({ loading: false });
        this.saveUserSettings(exportData);
        this.props.popSidePanel();
      }
    });
  }

  buildExportData() {
    const filename = `dealmachine-${this.state.export_type}-${moment().format("YYYY-MM-DD-HHmmss")}`;

    let contact_export_columns = [];
    let property_export_columns = [];

    if (this.state.export_type === "contacts") {
      contact_export_columns = this.state.selected_columns.map(column => column.slug);
    } else {
      property_export_columns = this.state.selected_columns.map(column => column.slug);
    }

    return {
      emails: this.state.emails,
      selected_leads: this.state.selected_leads,
      selected_columns: this.state.selected_columns,
      include_all_columns: this.state.include_all_columns,
      export_type: this.state.export_type,
      include_likely_owners: this.state.include_likely_owners,
      include_family: this.state.include_family,
      include_likely_renters: this.state.include_likely_renters,
      include_potential_property_owners: this.state.include_potential_property_owners,
      scrub_dnc: this.getScrubDncValue(),
      scrub_landline: this.state.scrub_landline,
      scrub_wireless: this.state.scrub_wireless,
      deduplicate: this.state.deduplicate,
      remove_items_without_phone_numbers: this.state.remove_items_without_phone_numbers,
      export_file_name: filename,
      contact_export_columns: contact_export_columns,
      property_export_columns: property_export_columns
    };
  }

  getScrubDncValue() {
    return this.props.user?.workspace_settings?.should_scrub_dnc_phone_numbers ||
      this.state.scrub_dnc;
  }

  saveUserSettings(exportData) {

    this.props.updateUserSettings({
      token: this.props.token,
      type: "export_settings",
      value: {
        export_type: exportData.export_type,
        include_likely_owners: exportData.include_likely_owners,
        include_family: exportData.include_family,
        include_likely_renters: exportData.include_likely_renters,
        include_potential_property_owners: exportData.include_potential_property_owners,
        scrub_dnc: exportData.scrub_dnc,
        scrub_landline: exportData.scrub_landline,
        scrub_wireless: exportData.scrub_wireless,
        deduplicate: exportData.deduplicate,
        remove_items_without_phone_numbers: exportData.remove_items_without_phone_numbers,
        export_file_name: exportData.export_file_name,
        user_column_preferences:
        {
          contact_export_columns: exportData.contact_export_columns,
          property_export_columns: exportData.property_export_columns
        }

      }
    });
  }

  handleBack() {
    dismissMobileKeyboard();
    this.props.export_modal?.cancelAction?.();
    this.props.popSidePanel();
  }

  checkSelectedCount() {
    return this.state.list_columns.filter(col => col.export || col.default).length;
  }

  setIncludeAllColumns(include_all_columns) {
    this.setState({ include_all_columns });
  }

  handleSelectColumns = () => {

    if (!this.state.selected_columns) {
      this.setState({ selected_columns: [] });
    }
    this.props.pushSidePanel({
      slug: "select_export_columns",
      overlay: true,
      data: {
        list_columns: this.state.list_columns,
        export_type: this.state.export_type,
        column_search: this.state.column_search,
        updateListItemFormat: this.updateListItemFormat.bind(this),
        onReorder: this.reorderItems.bind(this),
        setIncludeAllColumns: this.setIncludeAllColumns.bind(this),
        onSave: (selectedColumns, listColumns) => {
          this.setState({
            list_columns: listColumns || [],
            selected_columns: selectedColumns || []
          });
        }
      }
    });
  };


  renderFilterOptions() {
    const isDncLocked = this.props.user?.workspace_settings?.should_scrub_dnc_phone_numbers;

    return (
      <>
        <SelectItem
          selected={isDncLocked || this.state.scrub_dnc}
          disabled={isDncLocked}
          tooltip={isDncLocked ?
            "Your administrator has locked this setting. Only your administrator can change this setting." :
            ""
          }
          tooltipPlacement="top"
          onPress={() => !isDncLocked && this.setState({ scrub_dnc: !this.state.scrub_dnc })}
          select_type="toggle"
          description='Exclude contact phone numbers on the "Do Not Call" list.'
        >
          Scrub DNC
        </SelectItem>
        <SelectItem
          selected={this.state.scrub_landline}
          onPress={() => this.setState({ scrub_landline: !this.state.scrub_landline })}
          select_type="toggle"
          description="Exclude contact landline phone numbers."
        >
          Scrub Landline
        </SelectItem>
        <SelectItem
          selected={this.state.scrub_wireless}
          onPress={() => this.setState({ scrub_wireless: !this.state.scrub_wireless })}
          select_type="toggle"
          description="Exclude contact wireless phone numbers."
        >
          Scrub Wireless
        </SelectItem>
      </>
    );
  }

  renderContactTypeFilters() {
    const { colors } = this.props;

    return (
      <>
        <SmallLabel>Associated contacts to include:</SmallLabel>
        <Row style={{ paddingRight: 25, paddingLeft: 25, paddingBottom: 25, flexWrap: "wrap" }}>
          <InlineButton
            icon={this.state.include_likely_owners ? "check-box" : "check-box-outline-blank"}
            onPress={() => this.setState({ include_likely_owners: !this.state.include_likely_owners })}
            selected={this.state.include_likely_owners}
            style={this.state.include_likely_owners ? {
              borderColor: colors.active_color,
              borderWidth: 1,
              borderStyle: "solid",
              marginLeft: 0
            } : { marginLeft: 0 }}
            tooltip="The contact's address and name match the property's mailing address and owner listed in the county records."
            tooltipPlacement="top"
          >
            Likely Property Owners
          </InlineButton>

          <InlineButton
            icon={this.state.include_potential_property_owners && this.state.include_likely_owners ?
              "check-box" : "check-box-outline-blank"}
            onPress={() => this.setState({
              include_potential_property_owners: !this.state.include_potential_property_owners
            })}
            disabled={!this.state.include_likely_owners}
            selected={this.state.include_potential_property_owners && this.state.include_likely_owners}
            style={this.state.include_potential_property_owners ? {
              borderColor: colors.active_color,
              borderWidth: 1,
              borderStyle: "solid",
              marginLeft: 0
            } : { marginLeft: 0 }}
            tooltip={!this.state.include_likely_owners ?
              'You must have "Likely Property Owners" enabled to include potential property owners.' :
              "When we can't determine which contact is the property owner, we will include all contacts that are likely to be the property owner."
            }
            tooltipPlacement="top"
          >
            Potential Property Owners
          </InlineButton>

          <InlineButton
            icon={this.state.include_family ? "check-box" : "check-box-outline-blank"}
            onPress={() => this.setState({ include_family: !this.state.include_family })}
            selected={this.state.include_family}
            style={this.state.include_family ? {
              borderColor: colors.active_color,
              borderWidth: 1,
              borderStyle: "solid",
              marginLeft: 0
            } : { marginLeft: 0 }}
            tooltip="The contact's last name matches the property owner's name listed in the county records."
            tooltipPlacement="top"
          >
            Family Members
          </InlineButton>

          <InlineButton
            icon={this.state.include_likely_renters ? "check-box" : "check-box-outline-blank"}
            onPress={() => this.setState({ include_likely_renters: !this.state.include_likely_renters })}
            selected={this.state.include_likely_renters}
            style={this.state.include_likely_renters ? {
              borderColor: colors.active_color,
              borderWidth: 1,
              borderStyle: "solid",
              marginLeft: 0
            } : { marginLeft: 0 }}
            tooltip="The contact's name doesn't align with the listed property owner's, and they don't appear to be related. Additionally, their living address is different from the listed mailing address, suggesting they are a renter."
            tooltipPlacement="top"
          >
            Contacts Likely Renting
          </InlineButton>
        </Row>
      </>
    );
  }

  render() {
    const { colors, source_of_truth, device, export_modal, user } = this.props;

    if (!export_modal) return null;

    const currentLimit = parseInt(source_of_truth?.subscription?.metadata?.export_limit || 0);
    const currentCount = Math.min(
      parseInt(source_of_truth?.current_limits?.number_of_leads_exported?.amount || 0),
      currentLimit
    );

    return (
      <Container>
        <NewHeader
          title={export_modal.title}
          subtitle={export_modal.description}
          leftButton={{
            icon: device === "desktop" ? "close" : "arrow-back",
            onPress: () => this.handleBack()
          }}
        />
        {this.state.export_settings_loading ? (
          <SpinWrapper text="Loading..." />
        ) : (
          <>
            <InputBox
              input_ref={this._emails}
              autoFocus={false}
              name="emails"
              disabled={false}
              returnKeyType="done"
              placeholder={
                this.state.emails
                  ? "Email address to send export file"
                  : "Enter emails separated by a comma"
              }
              onFocus={() => { }}
              onChange={value => this.setState({ emails: value })}
              blurOnSubmit={true}
              value={this.state.emails}
              input_type="text"
              require_confirm={true}
              has_changed={
                !!this.state.emails &&
                (this.checkSelectedCount() > 0 || this.state.include_all_columns) &&
                (this.state.export_type === "properties" ||
                  this.state.include_likely_owners ||
                  this.state.include_family ||
                  this.state.include_likely_renters ||
                  this.state.include_potential_property_owners) &&
                !this.state.export_actual_count_loading
              }
              submit_button_primary={true}
              submit_button_title="Submit Export"
              onSubmit={() => this.confirmUpdate()}
              loading={this.state.loading}
            />

            <ExportLimitProgress
              count={currentCount}
              limit={currentLimit}
              resetDate={source_of_truth?.subscription?.monthly_limit_end_date}
              successColor={colors.success_color}
            />

            {user?.can_mimic_team_owner === 1 && !user?.workspace_id && (
              <Card>
                {this.state.submitted_sales_form ? (
                  <Wrapper style={{ padding: 25 }}>
                    <Copy style={{ textAlign: "center" }}>
                      Thanks for reaching out! We'll be in touch soon!
                    </Copy>
                  </Wrapper>
                ) : (
                  <SelectItem
                    noPress={true}
                    select_type="none"
                    renderRight={() => (
                      <InlineButton
                        loading={this.state.loading_sales_form}
                        primary={true}
                        onPress={() => {
                          this.props.submitMarketingForm({
                            token: this.props.token,
                            type: "contact_sales",
                            onLoading: () => this.setState({ loading_sales_form: true }),
                            onError: () => { },
                            onSuccess: () =>
                              this.setState({
                                loading_sales_form: false,
                                submitted_sales_form: true
                              })
                          });
                        }}
                      >
                        Contact Sales
                      </InlineButton>
                    )}
                    description="Contact us for custom pricing for larger scale operations."
                  >
                    Need more data? Have a larger team?
                  </SelectItem>
                )}
              </Card>
            )}

            <ExportTypeSelector
              exportType={this.state.export_type}
              export_settings_loading={this.state.export_settings_loading}
              onTypeChange={this.handleExportTypeChange}
              colors={colors}
              hasAccessToPeopleData={checkIfUserHasMetadata("people_data")}
              onUpgradePrompt={this.showUpgradePrompt}
              onSelectColumns={this.handleSelectColumns}
              device={device}
            />

            <KeyboardView style={{ flex: 1 }}>
              {this.state.export_type === "contacts" && this.renderContactTypeFilters()}

              {this.renderFilterOptions()}

              {this.state.export_type === "contacts" && (
                <SelectItem
                  selected={this.state.deduplicate}
                  onPress={() =>
                    this.setState({ deduplicate: !this.state.deduplicate })
                  }
                  select_type="toggle"
                  description="If a phone number or email address is already listed for another contact (usually in the same family) only include the first one."
                >
                  Deduplicate Contacts
                </SelectItem>
              )}

              {this.state.export_type === "contacts" && (
                <SelectItem
                  selected={!this.state.remove_items_without_phone_numbers}
                  onPress={() =>
                    this.setState({
                      remove_items_without_phone_numbers:
                        !this.state.remove_items_without_phone_numbers
                    })
                  }
                  select_type="toggle"
                  description="Disable to remove contacts without phone numbers from your export."
                >
                  Include contacts without phone numbers.
                </SelectItem>
              )}

              {this.state.export_type === "properties" && (
                <SelectItem
                  selected={!this.state.remove_items_without_phone_numbers}
                  onPress={() =>
                    this.setState({
                      remove_items_without_phone_numbers:
                        !this.state.remove_items_without_phone_numbers
                    })
                  }
                  select_type="toggle"
                  description="Disable to remove leads without phone numbers from your export."
                >
                  Include leads without phone numbers.
                </SelectItem>
              )}
            </KeyboardView>
          </>
        )}
      </Container>
    );
  }
}

const mapStateToProps = ({ auth, native, settings, lead, filter, billing }) => {
  const { token, user } = auth;
  const { platform, device, isMobile } = native;
  const { colors } = settings;
  const { export_modal } = lead;
  const { source_of_truth, all_feature_metadata } = billing;

  return {
    token,
    user,
    isMobile,
    platform,
    device,
    colors,
    export_modal,
    source_of_truth,
    all_feature_metadata
  };
};

export default connect(mapStateToProps, {
  appRedirect,
  setExportModal,
  popSidePanel,
  popAllSidePanels,
  updateLead,
  getSourceOfTruth,
  pushSidePanel,
  getExportActualCount,
  updateUserSettings,
  submitMarketingForm,
  getExportSettingsForUser
})(ExportLeads);